'use client'

import { useState, useEffect } from 'react'
import { useCurrency } from '@/lib/currency'
import Link from 'next/link'
import Navbar from '@/components/Navbar'
import { useAuth } from '@/context/AuthContext'

const FEATURES = [
  { label: 'Visible deals',           free: 'Limited',     plus: 'All deals',   pro: 'All deals' },
  { label: 'Home airports',           free: '1',           plus: '5',           pro: '15' },
  { label: 'Deal alerts (email)',              free: false,    plus: 'Daily',      pro: 'Real-time' },
  { label: 'Telegram alerts',                free: 'Top 5', plus: 'Daily',      pro: 'Real-time' },
  { label: 'Travel window alerts',           free: false,    plus: true,         pro: true },
  { label: 'Price history',           free: false,         plus: true,          pro: true },
  { label: 'Advanced filters',        free: false,         plus: true,               pro: true },
  { label: 'Budget filter',           free: false,         plus: true,               pro: true },
  { label: 'Trip duration filter',    free: false,         plus: true,               pro: true },
  { label: 'Destination wishlist',    free: false,         plus: '10',               pro: 'Unlimited' },
  { label: 'Deal score',              free: true,          plus: true,               pro: true },
  { label: 'Intercontinental deals',  free: false,         plus: false,              pro: true },
  { label: 'Travel calendar overlay',   free: false,         plus: false,              pro: true },
]

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    monthlyEur: 0,
    yearlyMonthlyEur: 0,
    yearlyTotal: null,
    sub: '1 airport. No card required.',
    savePct: null,
    popular: false,
    cta: 'Get started free',
    ctaHref: '/signup',
    ctaVariant: 'ghost',
    highlights: [
      'Limited deals',
      '1 home airport',
      'European deals only',
      'Daily Telegram digest (top 5)',
    ],
  },
  {
    id: 'plus',
    name: 'Plus',
    monthlyEur: 4.99,
    yearlyMonthlyEur: 2.92,
    yearlyTotalEur: 35,
    sub: '5 home airports. All deals.',
    savePct: 42,
    popular: true,
    cta: 'Get Plus',
    ctaHref: '/signup?from=%2Faccount%3Fupgrade%3Dplus',
    ctaVariant: 'primary',
    highlights: [
      'All deals, unlimited',
      '5 home airports',
      'Daily email + Telegram digest',
      'Priority alerts for your travel windows',
      'Price history & filters',
      'Budget & trip duration filters',
      'Destination wishlist (10 items)',
    ],
    callout: {
      headline: 'Never miss a deal from your home airport',
      partner: 'Daily alerts so you book when the price is right — not when it\'s gone',
      example: 'Most members save more per person on one booking than a full year of Plus',
    },
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyEur: 9.99,
    yearlyMonthlyEur: 4.08,
    yearlyTotalEur: 49,
    sub: '15 home airports. Global deals.',
    savePct: 59,
    popular: false,
    cta: 'Get Pro',
    ctaHref: '/signup?from=%2Faccount%3Fupgrade%3Dpro',
    ctaVariant: 'dark',
    callout: {
      headline: 'One deal pays for your entire year',
      partner: 'No more evenings comparing flights — we alert you when it\'s worth booking',
      example: 'Discover destinations you\'d never have searched for yourself',
    },
    highlights: [
      'Everything in Plus',
      '15 home airports',
      'Intercontinental deals (Asia, Americas, Africa, Oceania)',
      'Unlimited destination wishlist',
      'Deal score — AI-ranked by value',
      'Real-time Telegram alerts + quiet hours',
    ],
  },
]

const HOW_STEPS = [
  { icon: '🤖', title: 'Automated scanning, 24/7', body: 'FairFares monitors 3,000+ European routes and 570 intercontinental routes multiple times daily, compares against price history, and flags real drops. Our search algorithms are continuously optimised using machine learning — no manual curation, no guesswork.' },
  { icon: '🏠', title: 'Set your preferences', body: 'Add your home airport(s) so alerts match where you fly from. Optionally set a holiday calendar and flexible date range — FairFares only alerts you when the timing works.' },
  { icon: '📬', title: 'You get alerted', body: 'Get notified via email and Telegram when deals match your home airports. Plus sends a daily digest. Pro sends real-time alerts the moment a deal appears.' },
  { icon: '✈️', title: 'Choose your fair fare', body: 'You click through straight to the provider — airline or booking site. FairFares never marks up fares — you pay what the airline charges. Our ranking is never influenced by commission potential.' },
]

const FAQ_ITEMS = [
  {
    q: 'Is this a subscription?',
    a: 'Yes. Plus and Pro are monthly or yearly subscriptions that renew automatically. You can cancel at any time from your account page — you keep access until the end of your paid period.',
  },
  {
    q: 'Can I cancel?',
    a: 'Yes, at any time. Cancel via your account or the Stripe billing portal. After cancellation you keep access until the end of your paid period. No hassle.',
  },
  {
    q: "What's the difference between Plus and Pro?",
    a: 'Plus gives you all European deals, 5 home airports, a daily email + Telegram digest, price history, and filters. Pro adds intercontinental deals, 15 airports, real-time Telegram alerts (with quiet hours), deal score ranking, and an unlimited wishlist.',
  },
  {
    q: 'How are deals found?',
    a: 'FairFares is powered by ARAI — an autonomous AI system that scans 3,570+ routes 24/7 from 36 European airports, learns price patterns, and keeps improving itself. No sponsored results — only genuine discounts.',
  },
  {
    q: 'Do I have to book through FairFares?',
    a: 'No. We find the deals, you book directly with the airline. No middleman, no commission, no extra costs.',
  },
  {
    q: 'Can I switch plans?',
    a: 'Yes. You can upgrade or downgrade at any time. An upgrade takes effect immediately. A downgrade applies from the next billing period.',
  },
]


function BillingAdvice() {
  const [open, setOpen] = useState(false)
  return (
    <div className="mx-auto max-w-xl mt-4">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 mx-auto text-xs text-gray-400 hover:text-gray-600 transition-colors"
        aria-expanded={open}
      >
        <span>ℹ️</span>
        <span className="underline underline-offset-2">Monthly or yearly? Our honest take</span>
        <span className={`text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {open && (
        <div className="mt-3 rounded-xl bg-white border border-gray-200 px-5 py-4 text-xs text-gray-500 leading-relaxed">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="font-semibold text-gray-600 mb-1.5">Choose monthly if:</p>
              <ul className="space-y-1">
                <li>• You want to try FairFares before committing</li>
                <li>• You only travel in specific months (e.g. summer)</li>
                <li>• You prefer flexibility to cancel anytime</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold text-gray-600 mb-1.5">Choose yearly if:</p>
              <ul className="space-y-1">
                <li>• You travel 2+ times per year</li>
                <li>• You want the best price per month (save ~30%)</li>
                <li>• You want to monitor deals year-round — the best deals pop up at unexpected times</li>
              </ul>
            </div>
          </div>
          <p className="mt-3 text-gray-400 border-t border-gray-100 pt-3">
            💡 Tip: Many of the best deals appear in off-peak months. A yearly plan ensures you never miss them.
          </p>
        </div>
      )}
    </div>
  )
}

function SavingsCalculator() {
  const [trips, setTrips] = useState(2)
  const avgSaving = 150
  const proCost = 49
  const totalSaved = trips * avgSaving
  const roi = (totalSaved / proCost).toFixed(1)

  return (
    <section className="rounded-2xl bg-blue-50 border border-blue-100 px-6 py-6">
      <p className="text-center text-sm font-semibold text-blue-900 mb-1">💡 See how fast Pro pays for itself</p>
      <p className="text-center text-xs text-blue-600 mb-4">How many long-haul trips do you take per year?</p>
      <div className="flex justify-center gap-2 mb-5">
        {[1, 2, 3].map((n) => (
          <button
            key={n}
            onClick={() => setTrips(n)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold border transition-colors ${
              trips === n
                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                : 'bg-white text-gray-700 border-gray-200 hover:border-blue-400'
            }`}
          >
            {n === 3 ? '3+' : n}
          </button>
        ))}
      </div>
      <div className="mx-auto max-w-sm rounded-xl bg-white border border-blue-100 px-5 py-4 text-center">
        <p className="text-sm text-gray-700">
          <span className="font-semibold text-gray-900">{trips === 3 ? '3+' : trips} trip{trips !== 1 ? 's' : ''}</span>
          {' '}× €{avgSaving} avg. saving ={' '}
          <span className="font-semibold text-green-700">€{totalSaved}{trips === 3 ? '+' : ''} saved/year</span>
        </p>
        <p className="text-xs text-gray-500 mt-1">
          Pro costs €{proCost}/year →{' '}
          <span className="font-semibold text-blue-700">{roi}× return on your subscription</span>
        </p>
      </div>
    </section>
  )
}

function WhoIsItFor() {
  return (
    <section className="rounded-2xl bg-white border border-gray-200 px-6 py-8">
      <h2 className="text-center text-lg font-bold text-gray-900 mb-1">Who is FairFares for?</h2>
      <p className="text-center text-sm text-gray-500 mb-6 max-w-md mx-auto">
        The more flexible you are, the more you save. Even ±3 days on your dates opens up dramatically better fares.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div className="rounded-xl bg-green-50 border border-green-100 px-5 py-5">
          <p className="text-sm font-semibold text-green-800 mb-3">FairFares is perfect if you:</p>
          <ul className="space-y-2.5 text-sm text-gray-700">
            {[
              <>Have some flexibility in <strong>when</strong> you travel — even a few days can mean hundreds saved</>,
              <>Are open to <strong>where</strong> you go — the best deals decide your next destination</>,
              <>Travel with a <strong>partner or group</strong> — savings multiply per person</>,
              <>Love the thrill of spotting an unusually cheap fare</>,
              <>Book <strong>direct with airlines</strong> and want unbiased price context</>,
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="shrink-0 mt-0.5">✅</span>
                <span className="leading-snug">{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl bg-gray-50 border border-gray-200 px-5 py-5">
          <p className="text-sm font-semibold text-gray-600 mb-3">FairFares is probably not for you if:</p>
          <ul className="space-y-2.5 text-sm text-gray-700">
            {[
              'You must fly on exact dates to an exact destination (we can\'t help much there)',
              'You only fly business class on a single airline (our deals are mostly economy/mixed carrier)',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="shrink-0 mt-0.5">❌</span>
                <span className="leading-snug">{item}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-xs text-gray-500 border-t border-gray-200 pt-4 leading-relaxed">
            <strong className="text-gray-700">Be flexible, and you&apos;ll always find something that fits.</strong>{' '}
            FairFares shows you when those moments happen.
          </p>
        </div>
      </div>
    </section>
  )
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false)
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-5 py-4 text-left text-sm font-medium text-gray-800 hover:text-blue-600 transition-colors"
        aria-expanded={open}
      >
        {q}
        <span className={`ml-4 shrink-0 text-gray-400 text-xs transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>
      {open && (
        <p className="px-5 pb-4 text-sm text-gray-600 leading-relaxed">{a}</p>
      )}
    </div>
  )
}

function CheckIcon({ className = 'h-5 w-5 text-blue-600' }) {
  return (
    <svg className={`${className} shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}

function XIcon() {
  return (
    <svg className="h-5 w-5 text-gray-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

function Cell({ value, highlight }) {
  if (value === true)  return <span className="flex justify-center"><CheckIcon className={highlight ? 'h-5 w-5 text-blue-600' : 'h-5 w-5 text-gray-500'} /></span>
  if (value === false) return <span className="flex justify-center"><XIcon /></span>
  return <span className={`text-xs ${highlight ? 'text-blue-700 font-medium' : 'text-gray-600'}`}>{value}</span>
}

function PlanCard({ plan, yearly, stripeCustomerId, isCurrentPlan, proration }) {
  const { formatPlanPrice } = useCurrency()
  const { currentUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [checkoutError, setCheckoutError] = useState(null)
  const [confirming, setConfirming] = useState(false)
  const isFree = plan.id === 'free'
  // Confirmation needed when upgrading an existing subscription (no Stripe checkout page as safety net)
  const needsConfirm = !!stripeCustomerId && proration !== null && !isCurrentPlan

  async function doCheckout() {
    setLoading(true)
    setCheckoutError(null)
    setConfirming(false)
    const planKey = yearly ? `${plan.id}_yearly` : `${plan.id}_monthly`
    try {
      const res = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: planKey, userId: currentUser.uid, stripeCustomerId }),
      })
      const data = await res.json()
      if (data.url) window.location.href = data.url
      else if (data.upgraded) window.location.href = `/app/subscription?upgraded=${data.tier}`
      else {
        setLoading(false)
        setCheckoutError(data.error ?? 'Something went wrong. Please try again.')
      }
    } catch (e) { setLoading(false); setCheckoutError('Connection error. Please try again.') }
  }

  function handleCheckout() {
    if (!currentUser || isFree) return
    if (needsConfirm) { setConfirming(true); return }
    doCheckout()
  }

  function fmtEur(eurAmount) {
    if (eurAmount === 0) return '€0'
    return Number.isInteger(eurAmount) ? `€${eurAmount}` : `€${eurAmount.toFixed(2).replace('.', ',')}`
  }
  function approx(eurAmount) {
    return formatPlanPrice(eurAmount)
  }

  const monthlyEur = yearly && plan.yearlyMonthlyEur != null ? plan.yearlyMonthlyEur : plan.monthlyEur
  const price = fmtEur(monthlyEur)
  const priceHint = approx(monthlyEur)

  const ctaBase = 'block w-full text-center rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors'
  const ctaStyles = {
    ghost:   `${ctaBase} border border-gray-300 text-gray-700 hover:bg-gray-50`,
    primary: `${ctaBase} bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800`,
    dark:    `${ctaBase} bg-gray-900 text-white hover:bg-gray-800`,
  }

  return (
    <div className={`rounded-2xl border-2 bg-white p-6 flex flex-col ${plan.popular ? 'border-blue-600 shadow-lg shadow-blue-100' : 'border-gray-200'}`}>
      {plan.popular
        ? <div className="text-center mb-2"><span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Most Popular</span></div>
        : <div className="mb-2 h-[22px]" />
      }

      <p className={`text-xs font-semibold uppercase tracking-widest mb-2 ${plan.popular ? 'text-blue-600' : 'text-gray-400'}`}>
        {plan.name}
      </p>

      <div className="flex items-baseline gap-1 mb-0.5">
        <span className="text-4xl font-bold text-gray-900">{price}</span>
        {priceHint && <span className="ml-1.5 text-sm font-normal text-gray-400 self-end mb-1">({priceHint})</span>}
        {!isFree && <span className="text-sm text-gray-500">/mo</span>}
      </div>

      {!isFree && isCurrentPlan ? (
        <p className="text-sm font-semibold text-emerald-600 mb-1 min-h-[1.5rem]">✓ Current plan</p>
      ) : !isFree && proration ? (
        <p className="text-sm font-semibold text-blue-700 mb-1 min-h-[1.5rem]">
          {proration.amountDue > 0
            ? <>Today: €{(proration.amountDue / 100).toFixed(2).replace('.', ',')}
                <span className="text-xs font-normal text-gray-500 ml-1">(prorated · then {fmtEur(plan.monthlyEur)}/mo)</span>
              </>
            : 'No charge today — credit applied'
          }
        </p>
      ) : !isFree ? (
        <p className="text-sm text-gray-500 mb-1 min-h-[1.5rem]">
          {yearly
            ? <>Billed annually in EUR — {fmtEur(plan.yearlyTotalEur)}{approx(plan.yearlyTotalEur) ? ` (${approx(plan.yearlyTotalEur)})` : ''} / year</>
            : <>Billed monthly in EUR — or {fmtEur(plan.yearlyTotalEur)}{approx(plan.yearlyTotalEur) ? ` (${approx(plan.yearlyTotalEur)})` : ''} / yr</>
          }
          {!yearly && plan.savePct && (
            <span className="ml-1.5 inline-block bg-green-100 text-green-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">
              save {plan.savePct}%
            </span>
          )}
        </p>
      ) : (
        <div className="mb-1 min-h-[1.5rem]" />
      )}

      <p className="text-xs text-gray-400 mb-5">{plan.sub}</p>

      <ul className="space-y-2 mb-6 flex-1">
        {plan.highlights.map((item) => (
          <li key={item} className="flex items-start gap-2">
            <CheckIcon className="h-4 w-4 text-blue-500 mt-0.5" />
            <span className="text-sm text-gray-700 leading-snug">{item}</span>
          </li>
        ))}
      </ul>

      {plan.callout && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 mb-5 space-y-1.5">
          <p className="text-xs font-semibold text-amber-800">✈️ {plan.callout.headline}</p>
          <p className="text-xs text-amber-700">⏱ {plan.callout.partner}</p>
          <p className="text-xs text-amber-700">🌍 {plan.callout.example}</p>
        </div>
      )}

      {currentUser && !isFree && isCurrentPlan ? (
        <Link href="/app/subscription" className={`${ctaStyles.ghost} text-center`}>
          Manage subscription →
        </Link>
      ) : currentUser && !isFree && confirming ? (
        <div className="space-y-3">
          <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-900">
            <p className="font-semibold mb-1">Confirm upgrade to {plan.name}{yearly ? ' Yearly' : ' Monthly'}</p>
            <p>
              {proration && proration.amountDue > 0
                ? <>You'll be charged <strong>€{(proration.amountDue / 100).toFixed(2).replace('.', ',')}</strong> today (prorated). </>
                : <>No charge today — credit applied. </>
              }
              Then <strong>{fmtEur(monthlyEur)}/mo</strong>.
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={doCheckout} disabled={loading} className={`flex-1 ${ctaStyles[plan.ctaVariant]}`}>
              {loading ? 'Processing…' : 'Confirm'}
            </button>
            <button onClick={() => setConfirming(false)} className={`flex-1 ${ctaStyles.ghost}`}>
              Cancel
            </button>
          </div>
          {checkoutError && (
            <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{checkoutError}</p>
          )}
        </div>
      ) : currentUser && !isFree ? (
        <>
          <button onClick={handleCheckout} disabled={loading} className={ctaStyles[plan.ctaVariant]}>
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
                Processing…
              </span>
            ) : plan.cta}
          </button>
          {checkoutError && (
            <p className="mt-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{checkoutError}</p>
          )}
        </>
      ) : (
        <a href={isFree ? '/signup' : plan.ctaHref} className={ctaStyles[plan.ctaVariant]}>
          {plan.cta}
        </a>
      )}
    </div>
  )
}

export default function PricingPage() {
  const [yearly, setYearly] = useState(true)
  const { currentUser } = useAuth()
  const [stripeCustomerId, setStripeCustomerId] = useState(null)
  const [currentTier, setCurrentTier] = useState(null)
  const [currentInterval, setCurrentInterval] = useState(null)
  const [prorations, setProrations] = useState({})

  useEffect(() => {
    if (!currentUser) return
    import('@/lib/userProfile').then(({ getUserProfile }) =>
      getUserProfile(currentUser.uid)
        .then(p => {
          setStripeCustomerId(p?.stripeCustomerId ?? null)
          setCurrentTier(p?.tier ?? null)
        })
        .catch(() => {})
    )
  }, [currentUser])

  useEffect(() => {
    if (!stripeCustomerId) return
    const planKeys = yearly
      ? ['plus_yearly', 'pro_yearly']
      : ['plus_monthly', 'pro_monthly']
    fetch('/api/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'preview', customerId: stripeCustomerId, plans: planKeys }),
    })
      .then(r => r.json())
      .then(data => {
        if (typeof data === 'object' && !data.error) {
          setCurrentInterval(data._current ?? null)
          setProrations(data)
        }
      })
      .catch(() => {})
  }, [stripeCustomerId, yearly])

  return (
    <>
      <Navbar />

      <main className="min-h-screen bg-gray-50 px-4 py-10">
        <div className="mx-auto max-w-5xl space-y-12">

          {/* Hero */}
          <header className="text-center">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">
              Simple, honest pricing
            </h1>
            <p className="mt-3 text-gray-500 text-base max-w-md mx-auto leading-relaxed">
              No commissions. No upsells. Just great flight deals — straight to your inbox.
              Cancel anytime.
            </p>

            {/* Billing toggle */}
            <div className="mt-6 inline-flex items-center gap-1 bg-white border border-gray-200 rounded-full p-1">
              <button
                onClick={() => setYearly(false)}
                className={`text-sm font-medium px-4 py-1.5 rounded-full transition-colors ${
                  !yearly ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setYearly(true)}
                className={`flex items-center gap-1.5 text-sm font-medium px-4 py-1.5 rounded-full transition-colors ${
                  yearly ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Yearly
                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${yearly ? 'bg-blue-500 text-white' : 'bg-green-100 text-green-700'}`}>
                  −42%
                </span>
              </button>
            </div>

            {/* Billing advice */}
            <BillingAdvice />
          </header>

          {/* Savings calculator */}
          <SavingsCalculator />

          {/* Plan cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
            {PLANS.map((plan) => {
              const planKey = yearly ? `${plan.id}_yearly` : `${plan.id}_monthly`
              const isCurrentPlan = currentTier === plan.id && currentInterval === (yearly ? 'year' : 'month')
              return (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  yearly={yearly}
                  stripeCustomerId={stripeCustomerId}
                  isCurrentPlan={isCurrentPlan}
                  proration={prorations[planKey] ?? null}
                />
              )
            })}
          </div>

          {/* Cancel note */}
          <p className="text-center text-xs text-gray-400 -mt-8">
            Cancel before your next billing date. Access continues until the end of your current period.
          </p>

          {/* VAT notice */}
          <p className="text-center text-xs text-gray-400">
            Prices include 21% VAT for EU customers.
          </p>

          <p className="text-center text-xs text-gray-400">
            Wondering why we can charge this little?{' '}
            <Link href="/about#how-it-works" className="underline hover:text-gray-600">Read how FairFares works →</Link>
          </p>


          {/* How It Works */}
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-400 mb-6 text-center">
              How it works
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {HOW_STEPS.map(({ icon, title, body }, i) => (
                <div key={title} className="rounded-2xl bg-white border border-gray-200 px-6 py-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white text-xs font-bold">{i + 1}</span>
                    <p className="text-3xl leading-none">{icon}</p>
                  </div>
                  <p className="font-semibold text-gray-900 text-sm mb-1">{title}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Who is FairFares for */}
          <WhoIsItFor />

          {/* Feature comparison table */}
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-400 mb-4 text-center">
              Full comparison
            </h2>
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-3 py-2.5 font-semibold text-gray-700 w-2/5">Feature</th>
                    <th className="text-center px-2 py-2.5 font-semibold text-gray-500 w-1/5">Free</th>
                    <th className="text-center px-2 py-2.5 font-semibold text-blue-700 w-1/5">Plus</th>
                    <th className="text-center px-2 py-2.5 font-semibold text-gray-700 w-1/5">Pro</th>
                  </tr>
                </thead>
                <tbody>
                  {FEATURES.map(({ label, free, plus, pro }, i) => (
                    <tr key={label} className={i % 2 === 1 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-3 py-2.5 text-gray-700">{label}</td>
                      <td className="px-2 py-2.5 text-center"><Cell value={free} /></td>
                      <td className="px-2 py-2.5 text-center"><Cell value={plus} highlight /></td>
                      <td className="px-2 py-2.5 text-center"><Cell value={pro} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Reassurances */}
          <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            {[
              { emoji: '🔒', title: 'Secure payments', body: 'Processed by Stripe. We never see your card details.' },
              { emoji: '↩', title: 'Cancel anytime',  body: 'No lock-in. Cancel and keep access until period ends.' },
              { emoji: '✈️', title: 'No commissions',  body: 'We earn from subscriptions only — never from bookings.' },
            ].map(({ emoji, title, body }) => (
              <div key={title} className="rounded-xl bg-white border border-gray-200 px-4 py-5">
                <p className="text-2xl mb-2">{emoji}</p>
                <p className="font-semibold text-gray-800 text-sm">{title}</p>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">{body}</p>
              </div>
            ))}
          </section>

          {/* FAQ */}
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-400 mb-4 text-center">
              Frequently asked questions
            </h2>
            <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
              {FAQ_ITEMS.map(({ q, a }) => (
                <FaqItem key={q} q={q} a={a} />
              ))}
            </div>
          </section>

          {/* Earn blocks */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 p-5">
              <div className="text-2xl mb-2">🎁</div>
              <h3 className="text-sm font-bold text-gray-900 mb-1">Want it free? Just share.</h3>
              <p className="text-xs text-gray-600 leading-relaxed mb-4">
                Share your link with up to 3 friends. When they sign up, you both get a free Plus month — no credit card needed.
              </p>
              <Link href="/app/earn" className="inline-block bg-emerald-600 text-white text-xs font-semibold px-4 py-2 rounded-xl hover:bg-emerald-700 transition-colors">
                Get your share link →
              </Link>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-100 p-5">
              <div className="text-2xl mb-2">🤝</div>
              <h3 className="text-sm font-bold text-gray-900 mb-1">Keep sharing. Keep it free.</h3>
              <p className="text-xs text-gray-600 leading-relaxed mb-4">
                Each friend who subscribes earns you another free month — up to 24 months total. The more you share, the longer you fly for free.
              </p>
              <Link href="/app/earn" className="inline-block bg-violet-600 text-white text-xs font-semibold px-4 py-2 rounded-xl hover:bg-violet-700 transition-colors">
                Start earning →
              </Link>
            </div>
          </div>

          {/* FAQ link */}
          <div className="text-center pb-4">
            <p className="text-sm text-gray-500">
              Have questions?{' '}
              <Link href="/faq#pricing" className="text-blue-600 hover:underline font-medium">
                Read the FAQ
              </Link>
              {' '}or{' '}
              <a href="mailto:hello@fairfaresapp.com" className="text-blue-600 hover:underline font-medium">
                email us
              </a>
              .
            </p>
          </div>

        </div>
      </main>
    </>
  )
}
